/**
 * Russian render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'ru')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const russianLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'потеря_фокуса' },
    change: { primary: 'изменение' },
    click: { primary: 'клик' },
    dblclick: { primary: 'двойной_клик' },
    focus: { primary: 'фокус' },
    input: { primary: 'ввод' },
    keydown: { primary: 'клавиша_вниз' },
    keypress: { primary: 'нажатие_клавиши' },
    keyup: { primary: 'клавиша_вверх' },
    load: { primary: 'загрузка' },
    mousedown: { primary: 'мышьвниз' },
    mouseenter: { primary: 'мышь_вход' },
    mouseleave: { primary: 'мышь_выход' },
    mousemove: { primary: 'движение_мыши' },
    mouseout: { primary: 'уход' },
    mouseover: { primary: 'наведение' },
    mouseup: { primary: 'мышьвверх' },
    reset: { primary: 'сбросить' },
    resize: { primary: 'изменениеразмера' },
    scroll: { primary: 'прокрутка' },
    submit: { primary: 'отправка' },
    touchcancel: { primary: 'касание_отмена' },
    touchend: { primary: 'касание_конец' },
    touchmove: { primary: 'касание_движение' },
    touchstart: { primary: 'касание_начало' },
    unload: { primary: 'выгрузка' },
  },
  logical: {
    and: { primary: 'и' },
    changes: { primary: 'изменяется' },
    contains: { primary: 'содержит' },
    else: { primary: 'иначе' },
    empty: { primary: 'пустой' },
    end: { primary: 'конец' },
    equals: { primary: 'равно' },
    exists: { primary: 'существует' },
    has: { primary: 'имеет' },
    have: { primary: 'имею' },
    includes: { primary: 'включает' },
    is: { primary: 'есть' },
    isNot: { primary: 'не_есть' },
    live: { primary: 'живой' },
    matches: { primary: 'соответствует' },
    not: { primary: 'не' },
    or: { primary: 'или' },
    otherwise: { primary: 'в_противном_случае' },
    then: { primary: 'затем' },
    when: { primary: 'когда' },
    where: { primary: 'где' },
  },
  temporal: {
    every: { primary: 'каждый' },
    forever: { primary: 'всегда' },
    h: { primary: 'ч' },
    hour: { primary: 'час' },
    hours: { primary: 'часов' },
    millisecond: { primary: 'миллисекунда' },
    milliseconds: { primary: 'миллисекунд' },
    min: { primary: 'мин' },
    minute: { primary: 'минута' },
    minutes: { primary: 'минут' },
    ms: { primary: 'мс' },
    once: { primary: 'однажды' },
    s: { primary: 'с' },
    second: { primary: 'секунда' },
    seconds: { primary: 'секунд' },
    until: { primary: 'до' },
    times: { primary: 'раз' },
  },
  values: {
    body: { primary: 'тело' },
    closest: { primary: 'ближайший' },
    detail: { primary: 'детали' },
    document: { primary: 'документ' },
    element: { primary: 'элемент' },
    event: { primary: 'событие' },
    false: { primary: 'ложь' },
    first: { primary: 'первый' },
    it: { primary: 'это' },
    its: { primary: 'его' },
    last: { primary: 'последний' },
    me: { primary: 'я' },
    my: { primary: 'мой' },
    myself: { primary: 'я_сам' },
    next: { primary: 'следующий' },
    null: { primary: 'ничего' },
    parent: { primary: 'родитель' },
    previous: { primary: 'предыдущий' },
    result: { primary: 'результат' },
    target: { primary: 'цель' },
    true: { primary: 'истина' },
    undefined: { primary: 'неопределено' },
    value: { primary: 'значение' },
    window: { primary: 'окно' },
    you: { primary: 'ты' },
    your: { primary: 'твой' },
    yourself: { primary: 'сам' },
  },
  attributes: {
    attribute: { primary: 'атрибут' },
    attributes: { primary: 'атрибуты' },
    checked: { primary: 'отмечено' },
    class: { primary: 'класс' },
    classes: { primary: 'классы' },
    disabled: { primary: 'отключено' },
    hidden: { primary: 'скрыто' },
    html: { primary: 'html' },
    properties: { primary: 'свойства' },
    property: { primary: 'свойство' },
    readonly: { primary: 'только_чтение' },
    required: { primary: 'обязательно' },
    selected: { primary: 'выбрано' },
    style: { primary: 'стиль' },
    styles: { primary: 'стили' },
    text: { primary: 'текст' },
  },
  expressions: {
    up: { primary: 'вверх' },
    down: { primary: 'вниз' },
    left: { primary: 'влево' },
    right: { primary: 'вправо' },
    at: { primary: 'в' },
    back: { primary: 'назад' },
    characters: { primary: 'символы' },
    children: { primary: 'дети' },
    closest: { primary: 'ближайший' },
    elsewhere: { primary: 'снаружи' },
    empty: { primary: 'пустой' },
    'ends with': { primary: 'заканчивается_на' },
    exclusive: { primary: 'исключительно' },
    first: { primary: 'первый' },
    'ignoring case': { primary: 'без_учёта_регистра' },
    inclusive: { primary: 'включительно' },
    index: { primary: 'индекс' },
    'joined by': { primary: 'объединено_через' },
    last: { primary: 'последний' },
    'mapped to': { primary: 'преобразовано_в' },
    next: { primary: 'следующий' },
    no: { primary: 'нет' },
    parent: { primary: 'родитель' },
    prev: { primary: 'пред' },
    previous: { primary: 'предыдущий' },
    random: { primary: 'случайный' },
    some: { primary: 'некоторые' },
    'sorted by': { primary: 'сортировано_по' },
    'split by': { primary: 'разделено_по' },
    start: { primary: 'начать' },
    catch: { primary: 'поймать' },
    finally: { primary: 'наконец' },
    def: { primary: 'функция' },
    'starts with': { primary: 'начинается_с' },
    within: { primary: 'внутри' },
  },
};

registerLexicon('ru', russianLexicon);
